import { DEFAULT_LOCALE } from 'shared/i18n';
import { query } from 'utils/graphql';
import { LOAD_APP, LOAD_APP_FAIL, LOAD_APP_SUCCESS } from './app';

// Actions
const CREATE_TRANSLATION_TOKEN = 'voyager/company/CREATE_TRANSLATION_TOKEN';
const CREATE_TRANSLATION_TOKEN_SUCCESS = 'voyager/company/CREATE_TRANSLATION_TOKEN_SUCCESS';
const CREATE_TRANSLATION_TOKEN_FAIL = 'voyager/company/CREATE_TRANSLATION_TOKEN_FAIL';

const UPDATE_TRANSLATION_TOKEN = 'voyager/company/UPDATE_TRANSLATION_TOKEN';
const UPDATE_TRANSLATION_TOKEN_SUCCESS = 'voyager/company/UPDATE_TRANSLATION_TOKEN_SUCCESS';
const UPDATE_TRANSLATION_TOKEN_FAIL = 'voyager/company/UPDATE_TRANSLATION_TOKEN_FAIL';

const DELETE_TRANSLATION_TOKEN = 'voyager/company/DELETE_TRANSLATION_TOKEN';
const DELETE_TRANSLATION_TOKEN_SUCCESS = 'voyager/company/DELETE_TRANSLATION_TOKEN_SUCCESS';
const DELETE_TRANSLATION_TOKEN_FAIL = 'voyager/company/DELETE_TRANSLATION_TOKEN_FAIL';

const TRANSLATION_FIELDS = `
  id
  language
  enabled
  tokens {
    id
    key
    value
    defaultValue
  }
`;

// Reducer
const initialState = {
  loading: false,
  loaded: false,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD_APP:
      return {
        ...state,
        loading: true,
      };
    case LOAD_APP_FAIL:
      return {
        ...state,
        loaded: false,
        loading: false,
        error: action.error,
      };
    case LOAD_APP_SUCCESS:
      return {
        ...state,
        loaded: true,
        loading: false,
        ...action.data.company,
      };
    case CREATE_TRANSLATION_TOKEN:
    case UPDATE_TRANSLATION_TOKEN:
    case DELETE_TRANSLATION_TOKEN:
      return { ...state, saving: true, saveError: null };
    case CREATE_TRANSLATION_TOKEN_SUCCESS:
      return { ...state, saving: false, translations: action.data.createTranslationToken };
    case UPDATE_TRANSLATION_TOKEN_SUCCESS:
      return { ...state, saving: false, translations: action.data.updateTranslationToken };
    case DELETE_TRANSLATION_TOKEN_SUCCESS:
      return { ...state, saving: false, translations: action.data.deleteTranslationToken };
    case CREATE_TRANSLATION_TOKEN_FAIL:
    case UPDATE_TRANSLATION_TOKEN_FAIL:
    case DELETE_TRANSLATION_TOKEN_FAIL: {
      const err = action.error;
      const saveError = Array.isArray(err) ? err[0]?.message : err?.message;
      return { ...state, saving: false, saveError };
    }
    default:
      return state;
  }
}

// Helpers
const generateFieldReplacement = (key, label, value) => {
  if (!value) {
    return {};
  }

  return { [key]: { label, value } };
};

// Selectors
export const getCompany = state => state.company;

export const getCompanyId = state => state.company.id;

export const getCompanyName = state => state.company.name;

export const getCompanyAddress = state => state.company.address;

export const getCompanySettingsUrl = state => state.company.companySettingsUrl;

export const getCorporateUrl = state => state.company.corporateUrl;

export const getStorEdgeUrl = state => state.company.storEdgeUrl;

export const getLeadSources = state => state.company.sources;

export const getCompanyFieldReplacements = (state) => {
  if (!state.company) {
    return {};
  }

  const { company: { name, address, email, phone } } = state;

  return {
    ...generateFieldReplacement('companyName', 'Company Name', name),
    ...generateFieldReplacement('companyAddress', 'Company Address', address && address.fullAddress),
    ...generateFieldReplacement('companyEmail', 'Company E-mail', email),
    ...generateFieldReplacement('companyPhone', 'Company Phone', phone),
  };
};

export const getFlippers = state => state.company.flippers;

export const getFlipper = (state, flipper) =>
  state.company.flippers && state.company.flippers[flipper];

export const getLocales = state => state.company.locales;

export const getNonDefaultLocales = state => getLocales(state)
  .filter(locale => locale.code !== DEFAULT_LOCALE);

export const getLocaleCodes = state => getLocales(state)
  .map(locale => locale.code);

export const getNonDefaultLocaleCodes = state => getNonDefaultLocales(state)
  .map(locale => locale.code);

export const getTranslations = state => state.company.translations;
export const getTranslationsSaving = state => !!state.company.saving;
export const getTranslationsSaveError = state => state.company.saveError;

// Action Creators
export function createTranslationToken({ language, key, value, defaultValue }) {
  return {
    types: [CREATE_TRANSLATION_TOKEN, CREATE_TRANSLATION_TOKEN_SUCCESS, CREATE_TRANSLATION_TOKEN_FAIL],
    force: true,
    promise: query`
      mutation CreateTranslationToken(${{ language }}: String!, ${{ key }}: String!, ${{ value }}: String!, ${{ defaultValue }}: String) {
        createTranslationToken(language: $language, key: $key, value: $value, defaultValue: $defaultValue) {
          ${TRANSLATION_FIELDS}
        }
      }
    `,
  };
}

export function updateTranslationToken({ language, originalKey, key, value, defaultValue }) {
  return {
    types: [UPDATE_TRANSLATION_TOKEN, UPDATE_TRANSLATION_TOKEN_SUCCESS, UPDATE_TRANSLATION_TOKEN_FAIL],
    force: true,
    promise: query`
      mutation UpdateTranslationToken(${{ language }}: String!, ${{ originalKey }}: String!, ${{ key }}: String!, ${{ value }}: String!, ${{ defaultValue }}: String) {
        updateTranslationToken(language: $language, originalKey: $originalKey, key: $key, value: $value, defaultValue: $defaultValue) {
          ${TRANSLATION_FIELDS}
        }
      }
    `,
  };
}

export function deleteTranslationToken({ language, key }) {
  return {
    types: [DELETE_TRANSLATION_TOKEN, DELETE_TRANSLATION_TOKEN_SUCCESS, DELETE_TRANSLATION_TOKEN_FAIL],
    force: true,
    promise: query`
      mutation DeleteTranslationToken(${{ language }}: String!, ${{ key }}: String!) {
        deleteTranslationToken(language: $language, key: $key) {
          ${TRANSLATION_FIELDS}
        }
      }
    `,
  };
}

// Global
export function isLoaded(globalState) {
  return globalState.company && globalState.company.loaded;
}
